/* countdown-bar: standalone custom element for the Horizon section.
   Two modes: daily restart (next HH:MM in America/Bogota, HH:MM:SS)
   and specific date (target date/time in America/Bogota, DD:HH:MM:SS). */
(function () {
  'use strict';

  var STORAGE_PREFIX = 'hzn-cd:dismissed:';
  var STORE_TIME_ZONE = 'America/Bogota';
  var timeFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: STORE_TIME_ZONE,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
  });

  function storageKey(sectionId) { return STORAGE_PREFIX + sectionId; }

  function isDesignMode(host) {
    if (host.getAttribute('data-design-mode') === 'true') return true;
    try { return Boolean(window.Shopify && Shopify.designMode); } catch (e) { return false; }
  }

  function isDismissed(host) {
    if (isDesignMode(host)) return false;
    try {
      var fingerprint = host.getAttribute('data-fingerprint') || '';
      return Boolean(fingerprint && sessionStorage.getItem(storageKey(host.getAttribute('data-section-id') || '')) === fingerprint);
    } catch (e) { return false; }
  }

  function persistDismissed(host) {
    try {
      var fingerprint = host.getAttribute('data-fingerprint') || '';
      if (fingerprint) sessionStorage.setItem(storageKey(host.getAttribute('data-section-id') || ''), fingerprint);
    } catch (e) {}
  }

  function pad2(value) { return (value < 10 ? '0' : '') + value; }

  function storeDateParts(timestamp) {
    var parts = timeFormatter.formatToParts(new Date(timestamp));
    var values = {};
    for (var i = 0; i < parts.length; i += 1) values[parts[i].type] = parts[i].value;
    return {
      year: Number(values.year), month: Number(values.month), day: Number(values.day),
      hour: Number(values.hour), minute: Number(values.minute), second: Number(values.second)
    };
  }

  function storeOffset(timestamp) {
    var parts = storeDateParts(timestamp);
    return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second) - timestamp;
  }

  // Converts a wall-clock value in America/Bogota into its absolute timestamp.
  function storeDateTime(year, month, day, hour, minute, second) {
    var wallClock = Date.UTC(year, month - 1, day, hour, minute, second || 0);
    var timestamp = wallClock - storeOffset(wallClock);
    return wallClock - storeOffset(timestamp);
  }

  function parseDate(value) {
    var match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
    if (!match) return null;
    var year = Number(match[1]);
    var month = Number(match[2]);
    var day = Number(match[3]);
    var check = new Date(Date.UTC(year, month - 1, day));
    return check.getUTCFullYear() === year && check.getUTCMonth() === month - 1 && check.getUTCDate() === day ? { year: year, month: month, day: day } : null;
  }

  function parseTime(value) {
    var match = /^(\d{2}):(\d{2})$/.exec(value || '');
    if (!match) return null;
    var hour = Number(match[1]);
    var minute = Number(match[2]);
    return hour < 24 && minute < 60 ? { hour: hour, minute: minute } : null;
  }

  function msToDHMS(ms) {
    var totalSeconds = Math.max(0, Math.floor(ms / 1000));
    return {
      d: Math.floor(totalSeconds / 86400),
      h: Math.floor((totalSeconds % 86400) / 3600),
      m: Math.floor((totalSeconds % 3600) / 60),
      s: totalSeconds % 60,
      finished: ms <= 0
    };
  }

  function specificTarget(host) {
    var date = parseDate(host.getAttribute('data-end-date'));
    var time = parseTime(host.getAttribute('data-end-time'));
    return date && time ? storeDateTime(date.year, date.month, date.day, time.hour, time.minute) : null;
  }

  function dailyTarget(host, now) {
    var time = parseTime(host.getAttribute('data-daily-time'));
    if (!time) return null;
    var today = storeDateParts(now);
    var target = storeDateTime(today.year, today.month, today.day, time.hour, time.minute);
    if (target > now) return target;
    var tomorrow = new Date(Date.UTC(today.year, today.month - 1, today.day + 1));
    return storeDateTime(tomorrow.getUTCFullYear(), tomorrow.getUTCMonth() + 1, tomorrow.getUTCDate(), time.hour, time.minute);
  }

  // Legacy installs saved 'fixed' for the specific-date mode.
  function resolveMode(host) {
    var mode = host.getAttribute('data-mode') || 'daily';
    return mode === 'fixed' ? 'specific' : mode;
  }

  function valuesFor(host, now) {
    var mode = resolveMode(host);
    var target = null;
    if (mode === 'daily') target = dailyTarget(host, now);
    else if (mode === 'specific') target = specificTarget(host);
    else return null;
    if (target === null) return null;
    return msToDHMS(target - now);
  }

  class CountdownBar extends HTMLElement {
    connectedCallback() { this._init(); }
    disconnectedCallback() { this._clear(); }
    updatedCallback() { this._init(); }

    _init() {
      this._clear();
      if (isDismissed(this)) { this.setAttribute('hidden', ''); return; }
      this.removeAttribute('hidden');
      this._daysEl = this.querySelector('[data-unit="days"]');
      this._hoursEl = this.querySelector('[data-unit="hours"]');
      this._minutesEl = this.querySelector('[data-unit="minutes"]');
      this._secondsEl = this.querySelector('[data-unit="seconds"]');
      this._summaryEl = this.querySelector('[data-summary]');
      var button = this.querySelector('[data-close]');
      if (button && !button.hasAttribute('data-cd-bound')) {
        var self = this;
        button.setAttribute('data-cd-bound', '');
        button.addEventListener('click', function () { persistDismissed(self); self._clear(); self.setAttribute('hidden', ''); });
      }
      this._lastSummaryMinute = null;
      if (this._tick()) {
        var instance = this;
        this._interval = setInterval(function () { if (!instance._tick()) instance._clear(); }, 1000);
      }
    }

    _tick() {
      var mode = resolveMode(this);
      var values = valuesFor(this, Date.now());
      if (!values) {
        this._render(0, 0, 0, 0);
        this._renderSummary(0, 0, 0, true);
        return false;
      }
      this._render(values.d, values.h, values.m, values.s);
      var minuteKey = values.d * 1440 + values.h * 60 + values.m;
      if (values.finished || minuteKey !== this._lastSummaryMinute) {
        this._lastSummaryMinute = minuteKey;
        this._renderSummary(values.d, values.h, values.m, values.finished);
      }
      return !(mode === 'specific' && values.finished);
    }

    _render(days, hours, minutes, seconds) {
      if (this._daysEl) this._daysEl.textContent = pad2(days);
      if (this._hoursEl) this._hoursEl.textContent = pad2(hours);
      if (this._minutesEl) this._minutesEl.textContent = pad2(minutes);
      if (this._secondsEl) this._secondsEl.textContent = pad2(seconds);
    }

    _renderSummary(days, hours, minutes, finished) {
      if (!this._summaryEl) return;
      if (finished && resolveMode(this) === 'specific') { this._summaryEl.textContent = 'La oferta ha terminado.'; return; }
      var prefix = 'Tiempo restante: ';
      if (this._daysEl) prefix += days + ' ' + (this.getAttribute('data-label-days') || 'Días') + ', ';
      this._summaryEl.textContent = prefix + hours + ' ' + (this.getAttribute('data-label-hours') || 'Hrs') + ' y ' + minutes + ' ' + (this.getAttribute('data-label-minutes') || 'Mins') + '.';
    }

    _clear() { if (this._interval) { clearInterval(this._interval); this._interval = null; } }
  }

  if (!window.customElements.get('countdown-bar')) window.customElements.define('countdown-bar', CountdownBar);
})();

